import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { HealthBar, StatusPill, TextButton } from '@/lib';
import { goAsset } from '@/app/navigation';
import {
  FLEET_COLUMNS,
  loadColumnVisibility,
  rowsToCSV,
  saveColumnVisibility,
  type FleetColumnKey,
  type FleetRow,
} from './columns';

export type FleetTableProps = {
  rows: FleetRow[];
};

type SortState = { key: FleetColumnKey; dir: 'asc' | 'desc' };

/**
 * S2 Fleet table view. Plain (non-virtualized) sortable table; the
 * fleet asset count today is small enough that the virtualization
 * overhead in @tanstack/react-virtual wasn't worth adding as a new
 * dep. If lists grow into the thousands, swap the <tbody> mapping
 * for a virtualizer — the columns + row schema is already
 * declarative so the swap is local.
 */
export function FleetTable({ rows }: FleetTableProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState>({ key: 'severity', dir: 'asc' });
  const [vis, setVis] = useState(() => loadColumnVisibility());
  const [showColumns, setShowColumns] = useState(false);

  const visibleColumns = useMemo(
    () => FLEET_COLUMNS.filter((c) => vis[c.key]),
    [vis],
  );

  const sorted = useMemo(() => {
    const col = FLEET_COLUMNS.find((c) => c.key === sort.key);
    if (!col) return rows;
    const out = [...rows].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort]);

  const toggleVis = (key: FleetColumnKey) => {
    const next = { ...vis, [key]: !vis[key] };
    setVis(next);
    saveColumnVisibility(next);
  };

  const exportCSV = () => {
    const csv = rowsToCSV(sorted);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fleet.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-sp-3">
      <div className="flex flex-wrap items-center justify-between gap-sp-3">
        <p className="text-fs-meta text-gray-700">
          {sorted.length} asset{sorted.length === 1 ? '' : 's'} ·{' '}
          sorted by {FLEET_COLUMNS.find((c) => c.key === sort.key)?.label}{' '}
          ({sort.dir})
        </p>
        <div className="flex items-center gap-sp-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColumns((s) => !s)}
              className="rounded-r-chip border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-meta font-medium text-gray-900 hover:border-gray-500"
              aria-expanded={showColumns}
              aria-haspopup="menu"
            >
              Columns ({visibleColumns.length}/{FLEET_COLUMNS.length})
            </button>
            {showColumns ? (
              <ul
                role="menu"
                className="absolute right-0 z-10 mt-sp-1 flex flex-col gap-sp-1 rounded-r-card border border-gray-300 bg-gray-50 p-sp-3 text-fs-meta shadow"
              >
                {FLEET_COLUMNS.map((c) => (
                  <li key={c.key}>
                    <label className="flex items-center gap-sp-2 text-gray-900">
                      <input
                        type="checkbox"
                        checked={vis[c.key]}
                        onChange={() => toggleVis(c.key)}
                      />
                      {c.label}
                    </label>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <TextButton label="Export CSV" onClick={exportCSV} />
        </div>
      </div>

      <div className="overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
        <table className="min-w-full text-fs-meta">
          <thead>
            <tr>
              {visibleColumns.map((c) => {
                const isSort = sort.key === c.key;
                const nextDir = isSort && sort.dir === 'asc' ? 'desc' : 'asc';
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-gray-700"
                  >
                    <button
                      type="button"
                      onClick={() => setSort({ key: c.key, dir: nextDir })}
                      aria-sort={
                        isSort
                          ? sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      className="inline-flex items-center gap-sp-1 hover:text-gray-900"
                    >
                      {c.label}
                      {isSort ? (
                        <span aria-hidden>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                      ) : null}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr
                key={r.assetId}
                className="cursor-pointer border-t border-gray-300 hover:bg-gray-100"
                onClick={() => goAsset(navigate, r.assetId)}
              >
                {visibleColumns.map((c) => (
                  <td key={c.key} className="px-sp-3 py-sp-2 text-gray-900">
                    <CellContent col={c.key} row={r} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CellContent({ col, row }: { col: FleetColumnKey; row: FleetRow }) {
  switch (col) {
    case 'name':
      return <span className="font-medium">{row.name}</span>;
    case 'site':
      return <>{row.siteName}</>;
    case 'klass':
      return <>{row.assetClass}</>;
    case 'health':
      return (
        <div className="flex items-center gap-sp-2">
          <HealthBar
            size="row"
            value={row.currentHealthScore}
            previous={row.currentHealthPrev}
            thresholds={{ warn: 40, critical: 20 }}
          />
          <span className="font-mono text-gray-900">
            {Math.round(row.currentHealthScore)}
          </span>
        </div>
      );
    case 'rul':
      return (
        <span className="font-mono">
          {row.rul_days !== undefined ? `${row.rul_days}d` : '—'}
        </span>
      );
    case 'severity':
      return <StatusPill level={row.currentStatus} label={row.currentStatus} />;
    case 'open_alerts':
      return <span className="font-mono">{row.openAlertCount}</span>;
    case 'last_seen':
      return <>{row.last_seen ?? '—'}</>;
    default:
      return null;
  }
}
